import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation as useRouterLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLocation } from '../context/LocationContext';
import { createRestaurantSlug } from '../context/LocationContext';
import { useBrand } from '../context/BrandContext';
import apiService from '../services/apiService';
import { KitchenRowCard, LoadingSpinner, EmptyState } from '../components';
import { MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const { locationSlug } = useParams();
  const routerLocation = useRouterLocation();
  const { isAuthenticated, customer } = useAuth();
  const { cartItemCount } = useCart();
  const { selectedLocation, setSelectedLocation, getCurrentLocation, reverseGeocode, geocodeAddress } = useLocation();
  const { brand } = useBrand();

  // Brand-ops users (Nawabi / Bawarchi / Paradise) never need location-based filtering.
  // Their restaurants are determined entirely by the JWT brand claim on the server.
  const isBrandOpsUser = Boolean(brand);

  const [kitchens, setKitchens] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const urlSearchQuery = new URLSearchParams(routerLocation.search).get('q') || '';
  const [searchQuery, setSearchQuery] = useState(urlSearchQuery);

  // Sync search query from URL (navbar search)
  useEffect(() => {
    const q = new URLSearchParams(routerLocation.search).get('q') || '';
    setSearchQuery(q);
  }, [routerLocation.search]);
  const [vegMode, setVegMode] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const lastFetchedCityRef = useRef('__NEVER__');
  const catScrollRef = useRef(null);
  const [catScrollPos, setCatScrollPos] = useState(0);
  const [catMaxScroll, setCatMaxScroll] = useState(0);

  const fetchKitchens = useCallback(async (locationOverride) => {
    setIsLoading(true);
    try {
      const loc = locationOverride || selectedLocation;
      const apiParams = { limit: 50 };
      // For brand-ops users, restaurant visibility is controlled by the JWT brand claim
      // on the server — never send coordinates that could trigger distance-based logic.
      if (!isBrandOpsUser) {
        if (loc?.city) apiParams.city = loc.city;
        if (loc?.latitude) apiParams.lat = loc.latitude;
        if (loc?.longitude) apiParams.lon = loc.longitude;
      }

      const [kitchensRes, favRes] = await Promise.all([
        apiService.getKitchens(apiParams),
        isAuthenticated
          ? apiService.getFavorites().catch(() => ({ success: true, data: { favorites: [] } }))
          : Promise.resolve({ success: true, data: { favorites: [] } }),
      ]);

      if (kitchensRes?.success && kitchensRes?.data) {
        const list = kitchensRes.data.kitchens || kitchensRes.data || [];
        setKitchens(Array.isArray(list) ? list : []);
      }

      if (favRes?.success) {
        const favList = favRes.data?.favorites || favRes.data || [];
        setFavorites(new Set(favList.map(f => f.kitchen_id || f.id)));
      }
    } catch (e) {
      console.error('fetchKitchens error', e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocation, isAuthenticated, isBrandOpsUser]);

  // ── Sync URL slug → LocationContext ────────────────────────────────────────
  useEffect(() => {
    if (!locationSlug) return;

    const cityName = locationSlug
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

    // If slug matches AND we already have coordinates, nothing to do
    if (selectedLocation?.slug === locationSlug && selectedLocation?.latitude) return;

    // Set immediately with name so kitchens load
    if (selectedLocation?.slug !== locationSlug) {
      setSelectedLocation({ name: cityName, city: cityName, slug: locationSlug });
    }

    // Geocode to get precise lat/lng for distance calculations
    geocodeAddress(cityName).then(results => {
      if (results && results.length > 0) {
        const r = results[0];
        setSelectedLocation(prev => ({
          ...(prev || {}),
          name: cityName,
          city: cityName,
          slug: locationSlug,
          latitude: r.latitude,
          longitude: r.longitude,
          fullAddress: r.fullAddress,
        }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSlug]);

  // Re-fetch when location changes
  useEffect(() => {
    // Use city OR name so geocoded results without a city field still trigger a refetch
    const cityKey = selectedLocation?.city || selectedLocation?.name || null;
    if (cityKey !== lastFetchedCityRef.current) {
      lastFetchedCityRef.current = cityKey;
      setKitchens([]);
      fetchKitchens();
      
      // Ensure URL matches the selected location
      if (selectedLocation?.slug && (!locationSlug || locationSlug !== selectedLocation.slug)) {
        navigate(`/customer/${selectedLocation.slug}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  // Initial load
  useEffect(() => {
    if (lastFetchedCityRef.current === '__NEVER__') {
      if (isBrandOpsUser) {
        // Brand-ops users: skip GPS entirely, load all brand restaurants immediately
        lastFetchedCityRef.current = null;
        fetchKitchens();
      } else if (selectedLocation) {
        // Already have location from localStorage
        lastFetchedCityRef.current = selectedLocation.city || selectedLocation.name || null;
        fetchKitchens();
      } else {
        // Try GPS
        getCurrentLocation()
          .then(async (pos) => {
            const geocoded = await reverseGeocode(pos.latitude, pos.longitude);
            const loc = { ...geocoded, manuallySelected: false };
            setSelectedLocation(loc);
          })
          .catch(() => {
            lastFetchedCityRef.current = null;
            fetchKitchens();
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDetectLocation = async () => {
    setLocationLoading(true);
    try {
      const pos = await getCurrentLocation();
      const geocoded = await reverseGeocode(pos.latitude, pos.longitude);
      const loc = { ...geocoded, manuallySelected: true };
      setSelectedLocation(loc);
      setShowLocationModal(false);
      // Navigate to the new location URL
      const slug = loc.city
        ? loc.city.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').trim()
        : loc.name?.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').trim();
      if (slug) navigate(`/customer/${slug}`);
    } catch (e) {
      alert('Could not detect location. Please enter manually.');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleLocationSearch = async (query) => {
    setLocationSearchQuery(query);
    if (query.length < 2) { setLocationSuggestions([]); return; }
    const results = await geocodeAddress(query);
    setLocationSuggestions(results);
  };

  const handleSelectSuggestion = (suggestion) => {
    setSelectedLocation({ ...suggestion, manuallySelected: true });
    setShowLocationModal(false);
    setLocationSearchQuery('');
    setLocationSuggestions([]);
    // Navigate to the new location URL
    const slug = (suggestion.city || suggestion.name || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    if (slug) navigate(`/customer/${slug}`);
  };

  const handleToggleFavorite = async (kitchenId) => {
    if (!isAuthenticated) { navigate('/customer/login'); return; }
    const isFav = favorites.has(kitchenId);
    const newFavs = new Set(favorites);
    if (isFav) {
      newFavs.delete(kitchenId);
      await apiService.removeFromFavorites(kitchenId);
    } else {
      newFavs.add(kitchenId);
      await apiService.addToFavorites(kitchenId);
    }
    setFavorites(newFavs);
  };

  // Filter & Sort
  const RADIUS_MILES = 10;

  // Haversine distance helper
  const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 3958.8;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const userLat = selectedLocation?.latitude ? parseFloat(selectedLocation.latitude) : null;
  const userLng = selectedLocation?.longitude ? parseFloat(selectedLocation.longitude) : null;

  // Attach calculated distance to each kitchen for sorting/display
  const kitchensWithDistance = kitchens.map(k => {
    const kLat = k.coordinates_lat ? parseFloat(k.coordinates_lat) : null;
    const kLng = k.coordinates_lng ? parseFloat(k.coordinates_lng) : null;
    let distMiles = k.distance_miles != null ? parseFloat(k.distance_miles)
                  : k.distance != null ? parseFloat(k.distance)
                  : null;
    if (distMiles == null && userLat && userLng && kLat && kLng) {
      distMiles = haversine(userLat, userLng, kLat, kLng);
    }
    return { ...k, _distMiles: distMiles };
  });

  // Brand-ops users: never apply radius filtering — show ALL brand restaurants.
  // Regular users: apply 10-mile radius only when GPS coordinates are available.
  const radiusFiltered = (!isBrandOpsUser && userLat && userLng)
    ? kitchensWithDistance.filter(k => k._distMiles == null || k._distMiles <= RADIUS_MILES)
    : kitchensWithDistance;

  const filteredKitchens = radiusFiltered
    .filter((k) => {
      if (vegMode && !k.is_veg) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          (k.name || k.kitchen_name)?.toLowerCase().includes(q) ||
          k.cuisine_type?.toLowerCase().includes(q) ||
          k.description?.toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'rating') return (b.average_rating || 0) - (a.average_rating || 0);
      if (sortBy === 'distance') return (a._distMiles ?? 99) - (b._distMiles ?? 99);
      return 0;
    });

  // Category scroll helpers
  const scrollCats = (dir) => {
    const el = catScrollRef.current;
    if (!el) return;
    const amount = 240;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const updateCatScroll = () => {
    const el = catScrollRef.current;
    if (!el) return;
    setCatScrollPos(el.scrollLeft);
    setCatMaxScroll(el.scrollWidth - el.clientWidth);
  };

  // Extract unique cuisine categories from kitchens list
  const categories = [...new Set(
    kitchens.map(k => k.cuisine_type).filter(Boolean)
  )];

  return (
    <div className="home-page">
      {/* ── Categories horizontal scroll ────────────────────────────── */}
      {categories.length > 0 && (
        <div className="home-categories-section">
          {catScrollPos > 4 && (
            <button className="cat-scroll-btn cat-scroll-left" onClick={() => scrollCats('left')} aria-label="Scroll left">
              <ChevronLeft size={18} />
            </button>
          )}
          <div
            className="home-categories-track"
            ref={catScrollRef}
            onScroll={updateCatScroll}
          >
            <button
              className={`cat-chip ${!searchQuery ? 'active' : ''}`}
              onClick={() => setSearchQuery('')}
            >
              🍽️ All
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                className={`cat-chip ${searchQuery === cat ? 'active' : ''}`}
                onClick={() => setSearchQuery(searchQuery === cat ? '' : cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          {catScrollPos < catMaxScroll - 4 && (
            <button className="cat-scroll-btn cat-scroll-right" onClick={() => scrollCats('right')} aria-label="Scroll right">
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      )}

      {/* ── Kitchen List ─────────────────────────────────────────────── */}
      <div className="kitchen-list-section">
        {isLoading ? (
          <LoadingSpinner text="Finding kitchens near you..." />
        ) : filteredKitchens.length === 0 ? (
          <EmptyState
            icon="🍽️"
            title={
              isBrandOpsUser
                ? 'No kitchens are currently available'
                : (userLat && userLng ? 'No kitchens within 10 miles' : 'No kitchens found')
            }
            subtitle={
              isBrandOpsUser
                ? 'No kitchens are currently operational for your account.'
                : userLat && userLng
                  ? `There are no kitchens within a 10-mile radius of ${selectedLocation?.name || selectedLocation?.city || 'your location'}.`
                  : selectedLocation
                    ? `No kitchens available in ${selectedLocation.name || selectedLocation.city}`
                    : 'Select a location to find kitchens near you'
            }
            action={isBrandOpsUser ? undefined : { label: 'Change Location', onClick: () => setShowLocationModal(true) }}
          />
        ) : (
          <>
            <div className="section-title-row">
              <h2 className="section-title">Kitchens near you</h2>
              <div className="home-filter-controls">
                <div className="filter-select-wrapper">
                  <label className="filter-select-label">🥗 Type</label>
                  <select
                    className="filter-select"
                    value={vegMode ? 'veg' : 'all'}
                    onChange={(e) => setVegMode(e.target.value === 'veg')}
                  >
                    <option value="all">All</option>
                    <option value="veg">Veg Only</option>
                  </select>
                </div>
                <div className="filter-select-wrapper">
                  <label className="filter-select-label">↕ Sort By</label>
                  <select
                    className="filter-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="relevance">Relevance</option>
                    <option value="rating">Rating</option>
                    <option value="distance">Distance</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="kitchen-row-list">
              {filteredKitchens.map((kitchen, idx) => {
                const kid = kitchen.kitchen_id || kitchen.id;
                const getKitchenPath = () => {
                  const kitchenSlug = createRestaurantSlug(kitchen.kitchen_name || kitchen.name) || kid;
                  if (selectedLocation?.slug) {
                    return `/customer/${selectedLocation.slug}/${kitchenSlug}`;
                  }
                  return `/customer/kitchen/${kid}`;
                };
                return (
                  <KitchenRowCard
                    key={kid}
                    kitchen={{ ...kitchen, distance_miles: kitchen._distMiles ?? kitchen.distance_miles }}
                    index={idx}
                    onClick={() => navigate(getKitchenPath())}
                    onFavorite={isAuthenticated ? () => handleToggleFavorite(kid) : undefined}
                    isFavorite={favorites.has(kid)}
                    selectedLocation={selectedLocation}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Location Modal */}
      {showLocationModal && (
        <div className="modal-overlay" onClick={() => setShowLocationModal(false)}>
          <div className="modal-container modal-medium" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Select Location</h3>
              <button className="modal-close" onClick={() => setShowLocationModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <button
                className="detect-location-btn"
                onClick={handleDetectLocation}
                disabled={locationLoading}
              >
                <MapPin size={18} />
                {locationLoading ? 'Detecting...' : 'Use Current Location'}
              </button>

              <div className="location-divider">
                <span>or search manually</span>
              </div>

              <input
                type="text"
                className="form-input"
                placeholder="Search city, area, or address..."
                value={locationSearchQuery}
                onChange={(e) => handleLocationSearch(e.target.value)}
                autoFocus
              />

              {locationSuggestions.length > 0 && (
                <div className="location-suggestions">
                  {locationSuggestions.map((s, i) => (
                    <button
                      key={i}
                      className="location-suggestion-item"
                      onClick={() => handleSelectSuggestion(s)}
                    >
                      <MapPin size={16} /> {s.name}
                      <small>{s.fullAddress}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
